import { Button } from "@/components/ui/button.tsx";
import { useMemo, useRef, useState } from "react";
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog.tsx";
import ImagePicker, {
    type ProfileImagePickerHandle,
} from "@/components/custom/ImagePicker.tsx";
import { toast } from "sonner";
import { Spinner } from "@/components/ui/spinner.tsx";
import { type Team, useTeamQuery } from "@/store/teamStore.ts";
import { TeamPicture } from "@/components/custom/TeamPicture.tsx";
import { changeTeamIcon } from "@/api/teamApi.ts";
import { queryClient } from "@/store/queryConfig.ts";

type ChangeTeamPictureProps = {
    team: Team;
};

export function ChangeTeamPicture(props: ChangeTeamPictureProps) {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [ready, setReady] = useState(false);
    const [loading, setLoading] = useState(false);
    const pickerRef = useRef<ProfileImagePickerHandle | null>(null);
    const { data: teams, refetch: refetchTeams } = useTeamQuery();
    const currentTeam = useMemo(
        () => teams?.find((t) => t.id === props.team.id) ?? props.team,
        [teams, props.team],
    );

    return (
        <div>
            <h3 className="mb-4">Team Profile Picture</h3>
            <div className="flex flex-col w-full items-center gap-4">
                <TeamPicture className="w-20 h-20" team={currentTeam} />
                <Button className="w-full" onClick={() => setDialogOpen(true)}>
                    Change Team Picture
                </Button>
            </div>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Change Team Picture</DialogTitle>
                        <DialogDescription className="sr-only">
                            Select and crop an image to use as the team picture.
                        </DialogDescription>
                    </DialogHeader>
                    <div>
                        <ImagePicker ref={pickerRef} onReadyChange={setReady} />
                    </div>
                    <DialogFooter>
                        <DialogClose asChild>
                            <Button variant="tertiary">Cancel</Button>
                        </DialogClose>
                        <Button
                            disabled={!ready || loading}
                            onClick={async () => {
                                setLoading(true);
                                const file =
                                    await pickerRef.current?.exportCroppedFile();
                                if (!file) return;

                                const reader = new FileReader();
                                reader.onload = () => {
                                    if (!props.team) {
                                        toast.error(
                                            "There was an error changing the team picture. Please try again.",
                                        );
                                        return;
                                    }

                                    const base64String =
                                        reader.result as string;

                                    const previousTeams =
                                        queryClient.getQueryData<
                                            Team[] | undefined
                                        >(["teams"]);
                                    queryClient.setQueriesData<
                                        Team[] | undefined
                                    >(
                                        { queryKey: ["teams"] },
                                        (old) =>
                                            old?.map((t) =>
                                                t.id === props.team.id
                                                    ? {
                                                          ...t,
                                                          iconBase64:
                                                              base64String,
                                                      }
                                                    : t,
                                            ) ?? old,
                                    );

                                    changeTeamIcon(props.team.id, base64String)
                                        .then(() => {
                                            toast.success(
                                                "Team picture changed successfully.",
                                            );
                                            refetchTeams();
                                            setDialogOpen(false);
                                        })
                                        .catch(() => {
                                            queryClient.setQueriesData<
                                                Team[] | undefined
                                            >(
                                                { queryKey: ["teams"] },
                                                () => previousTeams,
                                            );
                                        })
                                        .finally(() => setLoading(false));
                                };
                                reader.readAsDataURL(file);
                            }}>
                            {loading ? <Spinner /> : <>Change</>}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
